import React, { useState } from 'react';
import { Env, ErrorBanner, Spinner, SuccessBanner } from '../../shared';
import { useResolveReport, useUserReports } from '../api/useUserReports';
import {
  CATEGORY_LABELS,
  NOTE_MAX,
  NOTE_MIN,
  ReportAction,
  ReportSideSnapshot,
  ReportStatus,
  URGENT_CATEGORIES,
  UserReport,
} from '../api/types';

/**
 * User reports filed from the consumer app (SSA-1579), for the report owner to
 * review: dismiss, remove the reported user from the in-venue stranger list, or
 * lift that removal. Every action needs a note and is written to the admin
 * audit log by the server.
 *
 * Reports are personal data about two users. They are rendered here and held
 * only in component state — never logged, never written to local storage.
 */

const STATUSES: { id: ReportStatus; label: string }[] = [
  { id: 'open', label: 'Open' },
  { id: 'dismissed', label: 'Dismissed' },
  { id: 'actioned', label: 'Actioned' },
];

const ACTION_COPY: Record<ReportAction, { label: string; confirm: string; done: string; tone: string }> = {
  dismiss: {
    label: 'Dismiss',
    confirm: 'Dismiss this report? Nothing changes for the reported user.',
    done: 'Report dismissed.',
    tone: 'bg-gray-800 text-gray-200 hover:bg-gray-700',
  },
  restrict: {
    label: 'Remove from stranger list',
    confirm:
      'Remove this user from the stranger list? They will no longer see or appear in the in-venue list, or be able to send requests to people on it. Everything else in the app keeps working.',
    done: 'Removed from the stranger list.',
    tone: 'bg-red-950/60 border border-red-800/50 text-red-300 hover:bg-red-900/50',
  },
  lift_restriction: {
    label: 'Lift restriction',
    confirm: 'Lift the restriction? This user can use the in-venue list again.',
    done: 'Restriction lifted.',
    tone: 'bg-emerald-950/60 border border-emerald-800/50 text-emerald-300 hover:bg-emerald-900/50',
  },
};

const RELATIONSHIP_LABELS: Record<string, string> = {
  friends: 'Friends',
  request_pending: 'Friend request pending',
  none: 'Not connected',
};

/** The actions a card offers: Open → dismiss/restrict; actioned and still
 *  restricted → lift; anything else → none. */
export function actionsFor(report: UserReport): ReportAction[] {
  if (report.status === 'open') return ['dismiss', 'restrict'];
  if (report.status === 'actioned' && report.reported.restricted) return ['lift_restriction'];
  return [];
}

function when(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

function sideSummary(side: ReportSideSnapshot | undefined): string {
  const venue = side?.venue ? side.venue.name || 'unnamed venue' : 'not checked in';
  const vibe = side?.vibe ?? '—';
  return `${venue} · vibe ${vibe}`;
}

function ReportedPhoto({ url, status }: { url: string | null | undefined; status: string | null | undefined }) {
  const [failed, setFailed] = useState(false);
  return (
    <div className="flex items-center gap-3">
      {url && !failed ? (
        <img
          src={url}
          alt="Reported user's profile"
          onError={() => setFailed(true)}
          className="w-16 h-16 rounded-lg object-cover border border-gray-700"
        />
      ) : (
        <div className="w-16 h-16 rounded-lg border border-gray-800 bg-gray-950 flex items-center justify-center text-[10px] text-gray-500 text-center px-1">
          {url ? 'photo no longer available' : 'no photo'}
        </div>
      )}
      <p className="text-xs text-gray-400">
        Photo status: <span className="text-gray-200">{status || 'none'}</span>
      </p>
    </div>
  );
}

function ReportCard({
  report,
  busy,
  onResolve,
}: {
  report: UserReport;
  busy: boolean;
  onResolve: (action: ReportAction, note: string) => Promise<boolean>;
}) {
  const [pending, setPending] = useState<ReportAction | null>(null);
  const [note, setNote] = useState('');
  const ctx = report.context ?? {};
  const urgent = URGENT_CATEGORIES.has(report.category);
  const trimmed = note.trim();
  const noteOk = trimmed.length >= NOTE_MIN && trimmed.length <= NOTE_MAX;

  const confirm = async () => {
    if (!pending || !noteOk) return;
    if (await onResolve(pending, trimmed)) {
      setPending(null);
      setNote('');
    }
  };

  return (
    <div
      data-testid="report-card"
      className={`bg-gray-900 border rounded-xl p-4 ${urgent ? 'border-red-700/70' : 'border-gray-800'}`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-semibold text-white">{CATEGORY_LABELS[report.category] ?? report.category}</p>
            {urgent && (
              <span className="px-2 py-0.5 text-[10px] font-bold uppercase rounded bg-red-900/60 text-red-200 border border-red-700">
                Urgent · same day
              </span>
            )}
            {report.reported.restricted && (
              <span className="px-2 py-0.5 text-[10px] font-medium rounded bg-amber-950/60 text-amber-300 border border-amber-800/60">
                Removed from stranger list
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-1">Filed {when(report.createdAt)}</p>
        </div>
        <p className="text-xs text-gray-400">
          {report.reported.openReportCount} open report{report.reported.openReportCount === 1 ? '' : 's'} about this user
        </p>
      </div>

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <p className="text-xs text-gray-500">Reported</p>
          <p className="text-sm text-white">{report.reported.displayName || 'no display name'}</p>
          <p className="font-mono text-[11px] text-gray-500">{report.reported.id}</p>
          <ReportedPhoto url={ctx.reported?.profilePicture} status={ctx.reported?.profilePictureStatus} />
        </div>
        <div className="space-y-1">
          <p className="text-xs text-gray-500">Reported by</p>
          <p className="text-sm text-white">
            {report.reporter ? report.reporter.displayName || 'no display name' : 'account erased'}
          </p>
          {report.reporter && <p className="font-mono text-[11px] text-gray-500">{report.reporter.id}</p>}
        </div>
      </div>

      <div className="mt-3 px-3 py-2 bg-gray-950 border border-gray-800 rounded-lg">
        <p className="text-xs text-gray-500">Reporter's note</p>
        <p className="text-sm text-gray-200 whitespace-pre-wrap mt-0.5">{report.details || 'No note.'}</p>
      </div>

      <div className="mt-3 text-xs text-gray-400 space-y-0.5">
        <p className="text-gray-500">At the moment of the report ({when(ctx.capturedAt)})</p>
        <p>Reporter: {sideSummary(ctx.reporter)}</p>
        <p>Reported: {sideSummary(ctx.reported)}</p>
        <p>
          Relationship: {RELATIONSHIP_LABELS[ctx.relationship ?? ''] ?? '—'} · friend request between them:{' '}
          {ctx.anyFriendRequest ? 'yes' : 'no'}
        </p>
      </div>

      {report.status !== 'open' && (
        <div className="mt-3 px-3 py-2 bg-gray-950 border border-gray-800 rounded-lg text-xs text-gray-300">
          <p>
            {report.status === 'dismissed' ? 'Dismissed' : 'Actioned'} by {report.resolvedByEmail || 'unknown'} ·{' '}
            {when(report.resolvedAt)}
          </p>
          {report.resolutionNote && <p className="text-gray-400 mt-0.5">{report.resolutionNote}</p>}
        </div>
      )}

      {pending === null ? (
        actionsFor(report).length > 0 && (
          <div className="flex flex-wrap gap-2 mt-4">
            {actionsFor(report).map((action) => (
              <button
                key={action}
                onClick={() => setPending(action)}
                disabled={busy}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg disabled:opacity-40 ${ACTION_COPY[action].tone}`}
              >
                {ACTION_COPY[action].label}
              </button>
            ))}
          </div>
        )
      ) : (
        <div className="mt-4 space-y-2">
          <p className="text-xs text-gray-300">{ACTION_COPY[pending].confirm}</p>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={NOTE_MAX}
            rows={2}
            aria-label="Resolution note"
            placeholder={`Note (required, ${NOTE_MIN}–${NOTE_MAX} characters) — recorded on the report and the audit log`}
            className="w-full px-3 py-2 bg-gray-950 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-violet-500"
          />
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <span className={`text-[11px] ${noteOk || trimmed.length === 0 ? 'text-gray-500' : 'text-amber-400'}`}>
              {trimmed.length}/{NOTE_MAX}
            </span>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  setPending(null);
                  setNote('');
                }}
                className="px-3 py-1.5 text-xs font-medium rounded-lg text-gray-400 hover:text-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={confirm}
                disabled={!noteOk || busy}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg disabled:opacity-40 ${ACTION_COPY[pending].tone}`}
              >
                Confirm {ACTION_COPY[pending].label.toLowerCase()}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function ReportsPanel({ env }: { env: Env }) {
  const [status, setStatus] = useState<ReportStatus>('open');
  const { reports, setReports, loading, error, refetch } = useUserReports(env, status);
  const { resolve, busyId } = useResolveReport(env);
  const [notice, setNotice] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const changeStatus = (next: ReportStatus) => {
    setNotice(null);
    setActionError(null);
    setStatus(next);
  };

  const onResolve = async (report: UserReport, action: ReportAction, note: string) => {
    setNotice(null);
    setActionError(null);
    const result = await resolve(report.id, action, note);
    if (!result.ok) {
      setActionError(result.message);
      // Someone else handled it, or it is gone: the list is stale.
      if (result.stale) refetch();
      return false;
    }
    // The response carries the reported user's live state (restricted, open
    // report count). Apply it to every card about the same person, so a
    // restriction made through one report shows on their others at once.
    const live = result.report.reported;
    setReports((prev) =>
      prev
        .filter((r) => r.id !== report.id || result.report.status === status)
        .map((r) => {
          if (r.id === report.id) return result.report; // still in this view (a lift)
          if (r.reported.id === live.id) {
            return {
              ...r,
              reported: { ...r.reported, restricted: live.restricted, openReportCount: live.openReportCount },
            };
          }
          return r;
        }),
    );
    setNotice(ACTION_COPY[action].done);
    return true;
  };

  const label = STATUSES.find((s) => s.id === status)?.label.toLowerCase();

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex gap-1">
          {STATUSES.map((s) => (
            <button
              key={s.id}
              onClick={() => changeStatus(s.id)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg ${
                status === s.id
                  ? 'bg-violet-950/40 text-violet-300 border border-violet-500/60'
                  : 'text-gray-400 hover:text-gray-200 hover:bg-gray-800/50'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <p className="text-sm text-gray-400">
            {loading ? 'Loading reports…' : `${reports.length} ${label} report${reports.length === 1 ? '' : 's'}`}
          </p>
          <button
            onClick={refetch}
            disabled={loading}
            className="px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-800 text-gray-200 hover:bg-gray-700 disabled:opacity-40"
          >
            Refresh
          </button>
        </div>
      </div>

      {notice && <SuccessBanner message={notice} />}
      {actionError && <ErrorBanner message={actionError} />}

      {error && (
        <div className="space-y-2">
          <ErrorBanner message={error} />
          <button
            onClick={refetch}
            className="px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-800 text-gray-200 hover:bg-gray-700"
          >
            Retry
          </button>
        </div>
      )}

      {loading && (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      )}

      {!loading && !error && reports.length === 0 && (
        <p className="text-sm text-gray-500 py-6 text-center">No {label} reports</p>
      )}

      <div className="space-y-3">
        {!loading &&
          reports.map((r) => (
            <ReportCard
              key={r.id}
              report={r}
              busy={busyId === r.id}
              onResolve={(action, note) => onResolve(r, action, note)}
            />
          ))}
      </div>
    </div>
  );
}
